
// client/src/components/Dashboard.jsx
// Optimized Dashboard with embedded ScheduledPostsList and ScheduledEventsList
// - Reduced number of API requests
// - Shows 3 most recent scheduled posts with "View All" button
// - Shows 3 most recent scheduled events with "View All" button

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { urlBase64ToUint8Array } from '../utils/pushUtils';
import { format, parseISO, isSameDay, addDays, startOfToday } from 'date-fns';
import MentionInbox from './MentionInbox';
import AppFooter from './AppFooter';
import { FaList, FaPlus, FaPaperPlane } from 'react-icons/fa';
import ScheduledPostsList from './media/ScheduledPostsList';
import ScheduledEventsList from './event/ScheduledEventsList';

const PUBLIC_VAPID_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function Dashboard() {
  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { tenant } = useParams();
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';

  // Resolve username reliably (works with your current storage pattern)
  function getUsernameSafe() {
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null') || {};
      const me = JSON.parse(localStorage.getItem('me') || 'null') || {};
      const s  = JSON.parse(localStorage.getItem('session') || 'null') || {};
      return (
        u.username ||
        s.username ||
        me.username ||
        (window.__ME__ && window.__ME__.username) ||
        localStorage.getItem('username') ||
        null
      );
    } catch { return localStorage.getItem('username') || null; }
  }
  const username = getUsernameSafe();
  
  // still expose id if you have it stored (not used by the tasks call)
  let userId = null;
  try { userId = (JSON.parse(localStorage.getItem('user')||'null')||{}).id || null; } catch {}

  // const today = startOfToday();
  // const currentDay = today;
  // const remainingDays = Array.from({ length: 6 }, (_, i) => addDays(today, i + 1));

  const today = React.useMemo(() => startOfToday(), []);
  const currentDay = today;
  const remainingDays = React.useMemo(
    () => Array.from({ length: 6 }, (_, i) => addDays(today, i + 1)),
    [today]
  );

  // PUSH SUBSCRIPTION EFFECT
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.ready
      .then(swReg => {
        return swReg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(PUBLIC_VAPID_KEY)
        });
      })
      .then(subscription => {
        return axios.post(`${baseUrl}/api/notifications/subscribe`, { subscription });
      })
      .catch(err => console.error('Push subscribe failed:', err));
  }, [baseUrl]);

  // OPTIMIZED: Combined data fetching in a single effect
  // Fetches events and tasks in parallel to reduce total request time
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const todayStr = format(today, 'yyyy-MM-dd');
        
        // Fetch events and tasks in parallel for better performance
        const [eventRes, taskRes] = await Promise.all([
          axios.get(`${baseUrl}/api/events/${tenant}/events`),
          axios.get(`${baseUrl}/api/events/${tenant}/tasks`, {
            params: { date: todayStr, username }
          })
        ]);
        
        if (eventRes.data.success) setEvents(eventRes.data.data || []);
        if (taskRes.data.success) setTasks(taskRes.data.tasks || []);
      } catch (err) {
        console.error('Error loading dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    if (!tenant) return;
    if (!username) { 
      setError('Failed to load dashboard data'); 
      setLoading(false); 
      return; 
    }
    fetchAll();
  }, [tenant, baseUrl, username]); // username is read synchronously at mount; dependencies included for clarity

  const handleDayClick = (day) => {
    setSelectedDay(day);
    setShowModal(true);
  };

  const closeModal = () => setShowModal(false);

  // Check if user is mentioned in a task
  const isUserMentioned = (task) => {
    const me = (username || '').toLowerCase();
    const created = (task.createdby || task.createdBy || '').toLowerCase();
    const comment = (task.comment || '').toLowerCase();
    return created === me || comment.includes(`@${me}`);
  };

  const isTimelineEvent = (event) => {
    const name = String(event?.name ?? event?.title ?? '').trim().toLowerCase();
    const type = String(event?.type ?? event?.eventtype ?? '').trim().toLowerCase();
    return name === 'timeline' || type === 'timeline';
  };

  const getEventsForDay = (day) => {
    return events.filter((event) => {
      // ✅ Hide Timeline event from Dashboard day views
      if (isTimelineEvent(event)) return false;

      // Defensive guards (prevents parseISO from throwing / producing Invalid Date)
      if (!event?.startdate || !event?.enddate) return false;

      const start = parseISO(event.startdate);
      const end = parseISO(event.enddate);

      if (isNaN(start) || isNaN(end)) return false;

      return day >= start && day <= end;
    });
  };

  // const getEventsForDay = (day) => {
  //   return events.filter(event => {
  //     const start = parseISO(event.startdate);
  //     const end = parseISO(event.enddate);
  //     return day >= start && day <= end;
  //   });
  // };

  const getTasksForDay = (day) => {
    return tasks.filter(task =>
      task.taskdate && isSameDay(new Date(task.taskdate), day) && isUserMentioned(task)
    );
  };

  const handleNewPost = () => {
    navigate(`/${tenant}/post/new`);
  };

  const rows = [];
  for (let i = 0; i < remainingDays.length; i += 2) {
    rows.push(remainingDays.slice(i, i + 2));
  }

  if (loading) return <p>Loading dashboard...</p>;
  if (error) return <p style={{ color: 'red' }}>{error}</p>;

  return (
    <div className="dashboard-container" style={{ paddingBottom: 72 }}>
      {/* Visible page header; inline style guards against global CSS hiding .hdr */}
      <h2
        className="hdr hdr--primary hdr--lg"
        style={{ display: 'block', margin: '12px 8px', color: '#000' }}
      >
        Dashboard
      </h2>

      {/* <MentionInbox userId={userId} /> */}

      <table className="day-table" style={{ width: '100%', tableLayout: 'fixed' }}>
        <tbody>
          {/* Current day */}
          <tr>
            <td colSpan="2" className="day-cell">
              <div className="day-card" onClick={() => handleDayClick(currentDay)}>
                <div className="task-table-wrapper">
                  <table className="task-table">
                    <tbody>
                      <tr>
                        <td className="day-header">{format(currentDay, 'EEEE')}</td>
                      </tr>
                      <tr>
                        <td className="day-subheader">{format(currentDay, 'MMMM do, yyyy')}</td>
                      </tr>

                      {[...getEventsForDay(currentDay), ...getTasksForDay(currentDay)]
                        .slice(0, 5)
                        .map((item, i) => (
                          <tr key={i}>
                            <td>{item.title || item.name}</td>
                          </tr>
                        ))}

                      {Array.from({
                        length:
                          7 -
                          (2 +
                            Math.min(
                              5,
                              getEventsForDay(currentDay).length + getTasksForDay(currentDay).length
                            )),
                      }).map((_, i) => (
                        <tr key={`empty-${i}`}>
                          <td>&nbsp;</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </td>
          </tr>

          {/* Scheduled Posts List - embedded in Dashboard */}
          {/* OPTIMIZED: Shows only the next 3 scheduled posts with "View All" button */}
          <tr>
            <td colSpan="2" className="day-cell">
              <div style={{ marginTop: '8px', marginBottom: '8px' }}>
                {/* Pass tenantId and embedded=true to limit to 3 posts and hide footer */}
                <ScheduledPostsList
                  tenantId={Number(tenant)}
                  embedded={true}
                />
              </div>
            </td>
          </tr>

          {/* Scheduled Events List - embedded in Dashboard */}
          {/* OPTIMIZED: Shows only the next 3 scheduled events with "View All" button */}
          <tr>
            <td colSpan="2" className="day-cell">
              <div style={{ marginTop: '8px', marginBottom: '8px' }}>
                {/* Pass tenantId and embedded=true to limit to 3 events, hide media, and hide footer */}
                <ScheduledEventsList
                  tenantId={Number(tenant)}
                  embedded={true}
                />
              </div>
            </td>
          </tr>

          {/* Remaining days with dots */}
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((day, index) => {
                const eventsToday = getEventsForDay(day);
                const tasksToday = getTasksForDay(day);
                const dotCount = Math.min(8, eventsToday.length + tasksToday.length);

                return (
                  <td key={index} className="day-cell">
                    <div className="day-card remaining-day" onClick={() => handleDayClick(day)}>
                      <div className="dots-container" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {[...Array(dotCount)].map((_, i) => (
                          <span
                            key={i}
                            style={{
                              width: 8,
                              height: 8,
                              backgroundColor: i < eventsToday.length ? '#ff6800' : '#3C3C3C',
                              borderRadius: '20%'
                            }}
                          />
                        ))}
                      </div>
                      <h4>{format(day, 'EEEE')}</h4>
                      <p>{format(day, 'MMMM do, yyyy')}</p>
                    </div>
                  </td>
                );
              })}
              {row.length < 2 && <td className="day-cell" />}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Modal with event + task list */}
      {showModal && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            top: 0, left: 0, width: '100%', height: '100%',
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 1000
          }}
        >
          <div
            className="modal-content"
            style={{
              background: '#fff',
              padding: '20px',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '80vh',
              overflowY: 'auto'
            }}
          >
            <h3>Details for {format(selectedDay, 'MMMM d, yyyy')}</h3>
            <ul>
              {getEventsForDay(selectedDay).map((event) => (
                <li key={`event-${event.id}`} style={{ marginBottom: '10px' }}>
                  <strong>{event.name}</strong>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => navigate(`/${tenant}/${event.id}/eventDetail`)}
                  >
                    View Event
                  </button>
                </li>
              ))}
              {getTasksForDay(selectedDay).map((task) => (
                <li key={`task-${task.id}`} style={{ marginBottom: '10px' }}>
                  <strong>{task.title}</strong>
                  <p>{task.comment}</p>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => {
                      const detailPath = task.eventitemid
                        ? `/${tenant}/${task.eventid}/${task.eventitemid}/eventItemDetail`
                        : `/${tenant}/${task.eventid}/eventDetail`;
                      navigate(detailPath);
                    }}
                  >
                    View Task
                  </button>
                </li>
              ))}
              {getEventsForDay(selectedDay).length === 0 && getTasksForDay(selectedDay).length === 0 && (
                <p>No events or tasks on this day.</p>
              )}
            </ul>
            <button onClick={closeModal} className="btn btn-secondary mt-3">Close</button>
          </div>
        </div>
      )}

      {/* Bottom action bar */}
      <AppFooter
        buttons={[
          {
            icon: <FaList />,
            label: 'Event List',
            onClick: () => navigate(`/${tenant}/events`),
          },
          {
                id: "new",
                icon: <FaPaperPlane />,
                label: "Post",
                onClick: handleNewPost,
                disabled: loading,
              },
          {
            icon: <FaPlus />,
            label: 'Add Event',
            onClick: () => navigate(`/${tenant}/eventAdd`),
          },
        ]}
      />
    </div>
  );
}

export default Dashboard;